import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import AuthLayout from "./AuthLayout";
import { api, getErrorMessage } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/api/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout>
        <div className="text-center" data-testid="forgot-sent">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/[0.12] grid place-items-center mb-5">
            <Mail className="text-primary" size={24} />
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Check your inbox</h2>
          <p className="mt-3 text-muted-foreground text-[14px] leading-relaxed">
            If an account exists for <span className="font-semibold text-foreground">{email}</span>,
            we've sent a password reset link. It expires in 1 hour. Also check your <strong>spam folder</strong>.
          </p>
          <p className="mt-3 text-[12px] text-muted-foreground">
            (DEV: check backend logs for the reset token.)
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block text-primary font-semibold text-[14px] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        <h2 className="text-2xl font-semibold text-foreground">Forgot password?</h2>
        <p className="text-muted-foreground text-[14px]">
          Enter your email and we'll send you a reset link.
        </p>
        <div>
          <label className="text-[13px] font-semibold text-foreground">Email address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email"
            className="mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
            placeholder="you@example.com"
          />
        </div>
        {err && (
          <div className="text-[13px] text-down bg-down/10 border border-down/30 rounded-lg px-4 py-3">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="forgot-submit"
          className="w-full py-3.5 rounded-lg brand-gradient hover:brightness-110 transition text-primary-foreground font-semibold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Send reset link
        </button>
        <Link
          to="/login"
          className="block text-center text-[13px] text-muted-foreground hover:text-primary"
        >
          ← Back to sign in
        </Link>
      </form>
    </AuthLayout>
  );
}
